import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  trans,
  useSoyuzExperiment,
  useSoyuzFlag,
  useSoyuzLocales
} from '@spotahome/soyuz/client';
import { Colors, MediaQuery, TrustpilotWidget } from '@spotahome/ui-library';
import HistoricalMetricsRedesign from '@spotahome/ui-library/src/tenant/components/HistoricalMetricsRedesign';
import CityAutocompleter from '@spotahome/ui-library/src/tenant/components/CityAutocompleter';
import { TEMPLATE_IDS } from '@spotahome/ui-library/src/tenant/components/TrustpilotWidget/TrustpilotWidget';

import HeroContainer from '@spotahome/landings-common/src/components/HeroContainer';
import HeaderContainer from '@spotahome/landings-common/src/components/HeaderContainer';

import withInViewEvent from '@spotahome/ui-library/src/tenant/components/withInViewEvent';

import { LANDING_SECTIONS } from '@spotahome/ui-library/src/tenant/components/utils/seoConstants';

import { HeroAgenticInput } from './HeroAgenticInput';

import './Hero.scss';

const { isMobileScreen, isPhabletScreen, isTabletScreen } = MediaQuery;

const Hero = ({ deviceType, carouselCities = [] }) => {
  const [isDark, setIsDark] = useState(true);
  const { current: locale } = useSoyuzLocales();

  useEffect(() => {
    setIsDark(isMobileScreen() || isPhabletScreen() || isTabletScreen());
  }, []);

  const isHeaderRedesignVariant =
    useSoyuzExperiment('HeaderRedesignV2').get('behaviour') === 'variant';

  const hideTrustpilot = useSoyuzFlag('HideTrustpilotHomepage').isOn();
  const showAgenticInput = useSoyuzFlag(
    'ConversationalSearchPlayground'
  ).isOn();

  const headerWrapperClasses = classNames('hero-tenant__header-wrapper', {
    'hero-tenant__header-wrapper--redesign': isHeaderRedesignVariant
  });

  return (
    <section>
      <HeroContainer>
        <div className={headerWrapperClasses}>
          <HeaderContainer
            color={{
              foregroundColor: isDark ? 'dark' : 'light',
              logoColor: isDark ? 'dark' : 'light',
              background: !isDark ? 'light' : 'transparent'
            }}
            tracking="ga-home-tenant"
          />
        </div>
        <div className="hero-tenant__wrapper">
          <div className="hero-tenant__container hero-tenant__container--blue-background">
            <div className="hero-tenant__info hero-tenant__info--blue">
              <div className="hero-tenant__info-content">
                <h1 className="hero-tenant__info-title hero-tenant__info-title--smaller">
                  {trans('home.tenant.hero.title.security')}
                </h1>
                <h2 className="hero-tenant__info-subtitle hero-tenant__info-subtitle--lighter">
                  {trans('home.tenant.hero.subtitle.security')}
                </h2>
                {showAgenticInput ? (
                  <HeroAgenticInput cities={carouselCities} />
                ) : (
                  <CityAutocompleter
                    deviceType={deviceType}
                    className="hero-tenant__city-selector hero-tenant__city-selector--bigger"
                    iconColor={Colors.medium}
                  />
                )}
                {hideTrustpilot ? (
                  <div style={{ height: '120px' }} />
                ) : (
                  <TrustpilotWidget
                    locale={locale}
                    className="hero-tenant__trustpilot-wrapper-redesign"
                    template={TEMPLATE_IDS.MICRO_TRUST_SCORE}
                  />
                )}
              </div>
            </div>
            <div className="hero-tenant__container-bottom" />
          </div>
        </div>
      </HeroContainer>
      <HistoricalMetricsRedesign className="hero-tenant__historical-metrics--grey" />
    </section>
  );
};

Hero.propTypes = {
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  carouselCities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      cityName: PropTypes.string.isRequired
    })
  )
};

export default withInViewEvent(LANDING_SECTIONS.hero)(Hero);
